import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getGalleryImages } from "../services/galleryService";
import "./gallery.css";

const SECTION_META = {
  womensday: {
    title: "Women's Day Celebration",
    description: "Empowerment, leadership, and celebration of women shaping YUVA.",
    icon: "💫",
  },
  meetings: {
    title: "YUVA Meetings",
    description: "Strategic sessions, brainstorms, and community connections.",
    icon: "🎯",
  },
  "mental-health-awareness": {
    title: "Mental Health Awareness",
    description: "Creating spaces for wellbeing and support.",
    icon: "🌱",
  },
  general: {
    title: "Moments & Milestones",
    description: "The unforgettable journey of YUVA's innovation.",
    icon: "✨",
  },
};

const toCategoryTitle = (category) =>
  String(category || "general")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "General";

const toOptimizedCloudinaryUrl = (url) => {
  if (!url || !url.includes("/image/upload/")) return url;
  return url.replace("/image/upload/", "/image/upload/f_auto,q_auto/");
};

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const data = await getGalleryImages();
        setImages(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, []);

  const sections = useMemo(() => {
    const grouped = {};

    images.forEach((image) => {
      const category = image.category || "general";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(image);
    });

    return Object.entries(grouped).map(([key, sectionImages]) => ({
      key,
      title: SECTION_META[key]?.title || toCategoryTitle(key),
      description:
        SECTION_META[key]?.description ||
        `${toCategoryTitle(key)} highlights from YUVA activities.`,
      icon: SECTION_META[key]?.icon || "📸",
      images: sectionImages,
    }));
  }, [images]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, filter: "blur(10px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Navbar />

      <main className="gallery-page">
        {/* Atmospheric Background */}
        <div className="gallery-atmosphere">
          <div className="glow-orb glow-orb-1" />
          <div className="glow-orb glow-orb-2" />
          <div className="glow-orb glow-orb-3" />
        </div>

        <motion.div
          className="gallery-container"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <motion.section className="gallery-hero" variants={itemVariants}>
            <div className="hero-content">
              <motion.div
                className="hero-accent-line"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              />
              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Moments That
                <br />
                <span className="hero-gradient">Built YUVA</span>
              </motion.h1>
              <motion.p
                className="hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Explore leadership journeys, innovation showcases, events, collaborations, milestones, and unforgettable memories that define our ecosystem.
              </motion.p>
            </div>
          </motion.section>

          {/* Content Sections */}
          {loading ? (
            <motion.section
              className="loading-state"
              variants={itemVariants}
            >
              <div className="pulse-loader" />
              <p>Preparing your gallery experience...</p>
            </motion.section>
          ) : sections.every((section) => section.images.length === 0) ? (
            <motion.section
              className="empty-state-premium"
              variants={itemVariants}
            >
              <div className="empty-icon">🎬</div>
              <h3>New Stories Are Being Captured</h3>
              <p>
                The YUVA journey is being documented. Check back soon for our latest moments and milestones.
              </p>
            </motion.section>
          ) : (
            <motion.div
              className="gallery-sections"
              variants={containerVariants}
            >
              {sections.map((section) => (
                section.images.length > 0 && (
                  <motion.section
                    key={section.key}
                    className="gallery-section"
                    variants={itemVariants}
                  >
                    {/* Section Header */}
                    <div className="section-header">
                      <div className="section-title-wrap">
                        <span className="section-icon">{section.icon}</span>
                        <div>
                          <h2 className="section-title">{section.title}</h2>
                          <p className="section-description">{section.description}</p>
                        </div>
                      </div>
                      <span className="image-count">
                        {section.images.length} image{section.images.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="section-divider" />

                    {/* Masonry Grid */}
                    <motion.div
                      className="gallery-grid"
                      variants={containerVariants}
                    >
                      {section.images.map((image, index) => (
                        <GalleryCard
                          key={image._id || `${section.key}-${index}`}
                          image={image}
                          section={section}
                          index={index}
                          itemVariants={itemVariants}
                          onImageClick={setSelectedImage}
                        />
                      ))}
                    </motion.div>
                  </motion.section>
                )
              ))}
            </motion.div>
          )}
        </motion.div>

        {/* Fullscreen Modal */}
        <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />
      </main>

      <Footer />
    </>
  );
};

const GalleryCard = ({ image, section, index, itemVariants, onImageClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Dynamic aspect ratios for masonry effect (kept for future enhancement)

  return (
    <motion.div
      className={`gallery-item-wrapper ${getItemSize(index)}`}
      variants={itemVariants}
      custom={index}
      initial="hidden"
      animate="visible"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onImageClick(image)}
    >
      <motion.article
        className="gallery-item"
        animate={isHovered ? "hover" : "rest"}
        variants={{
          rest: { y: 0, scale: 1 },
          hover: { y: -6, scale: 1.02 },
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="item-glow-bg" />

        <motion.img
          src={toOptimizedCloudinaryUrl(image.imageUrl)}
          alt={image.title || `${section.title} ${index + 1}`}
          loading="lazy"
          animate={isHovered ? "hover" : "rest"}
          variants={{
            rest: { scale: 1 },
            hover: { scale: 1.08 },
          }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />

        {/* Overlay */}
        <motion.div
          className="item-overlay"
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="overlay-content">
            {image.title && (
              <h4 className="image-title">{image.title}</h4>
            )}
            <span className="view-icon">↗</span>
          </div>
        </motion.div>

        {/* Corner Accent */}
        <div className="item-corner-accent" />
      </motion.article>
    </motion.div>
  );
};

const getItemSize = (index) => {
  const sizes = [
    "large",
    "medium",
    "small",
    "medium",
    "large",
    "small",
    "medium",
  ];
  return sizes[index % sizes.length];
};

const ImageModal = ({ image, onClose }) => {
  if (!image) return null;

  return (
    <motion.div
      className="image-modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="image-modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>✕</button>
        <img
          src={toOptimizedCloudinaryUrl(image.imageUrl)}
          alt={image.title || "Gallery image"}
          className="modal-image"
        />
        {image.title && <p className="modal-title">{image.title}</p>}
      </motion.div>
    </motion.div>
  );
};

export default Gallery;
